import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { axiosPrivate } from '../../api/axios';
import { Box, Card, Button, CardHeader, CardContent, IconButton, useTheme, Tooltip, Typography, Alert } from '@mui/material';
import Grid from "@mui/material/Grid2";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Icon } from '../icons';
import MasterAgGrid from '../common/DataGrid/MasterAgGrid';
import UserDialog from './UserDialog';

import API_URL from '../../utils/API_URL';

// 편집 아이콘 셀 렌더러
const EditCellRenderer = (props) => {
  const onClick = (e) => {
    e.stopPropagation();
    if (props.onEdit) props.onEdit(props.data);
  };
  return (
    <span onClick={onClick} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
      <Icon name="editIcon" style={{ width: 18, height: 18 }} />
    </span>
  );
};

// 관리자 표시 셀 렌더러
const AdminCellRenderer = (props) => {
  if (props.value !== 1) return null;
  return (
    <Tooltip title="그룹 관리자">
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <Icon name="shieldCheckIcon" style={{ width: 16, height: 16, color: 'var(--mui-palette-success-main)' }} />
        <span style={{ fontSize: 12, color: 'var(--mui-palette-success-main)', fontWeight: 600 }}>ADMIN</span>
      </span>
    </Tooltip>
  );
};

const UsersPage = () => {
  const theme = useTheme();
  const gridRef = useRef();
  const [users, setUsers] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [quota, setQuota] = useState({ project_limit: 0, user_limit: 0, project_count: 0, user_count: 0 });
  const [error, setError] = useState('');

  const handleEditUser = useCallback((row) => {
    setSelectedUser(row);
    setDialogOpen(true);
  }, []);

  const columnDefs = useMemo(() => [
    { field: 'is_admin', headerName: '관리자', minWidth: 110, width: 110, sortable: false, filter: false, cellRenderer: AdminCellRenderer, cellClass: 'ag-cell-center' },
    { field: 'login_id', headerName: '아이디', minWidth: 140 },
    { field: 'user_name', headerName: '이름', minWidth: 140 },
    { field: 'email', headerName: '이메일', minWidth: 220 },
    { field: 'edited_at', headerName: '편집일', minWidth: 170 },
    { field: 'registered_at', headerName: '생성일', minWidth: 170 },
    {
      headerName: '편집',
      field: 'actions',
      minWidth: 90,
      width: 90,
      sortable: false,
      filter: false,
      suppressMenu: true,
      cellClass: 'ag-cell-center',
      cellRenderer: EditCellRenderer,
      cellRendererParams: { onEdit: handleEditUser },
    },
  ], [handleEditUser]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axiosPrivate.post(`${API_URL}/auth/admin/getUserList`);
      const data = (res.data || []).map((row) => ({ ...row, id: row.login_id }));
      // 서버에서 is_admin DESC로 정렬하지만, 이중 보정
      data.sort((a, b) => (b.is_admin - a.is_admin) || 0);
      setUsers(data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchGroupQuota = useCallback(async () => {
    try {
      const res = await axiosPrivate.post('/project/getGroupQuota');
      const q = res.data || {};
      setQuota({
        project_limit: Number.isFinite(q.project_limit) ? q.project_limit : 0,
        user_limit: Number.isFinite(q.user_limit) ? q.user_limit : 0,
        project_count: Number.isFinite(q.project_count) ? q.project_count : 0,
        user_count: Number.isFinite(q.user_count) ? q.user_count : 0,
      });
    } catch (err) {
      console.error('그룹 한도 조회 실패:', err);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchGroupQuota();
  }, [fetchUsers, fetchGroupQuota]);

  const handleNewUser = () => {
    setSelectedUser(null);
    setDialogOpen(true);
  };

  const handleDeleteUsers = async () => {
    if (selectedRows.length === 0) return;
    if (!window.confirm(`선택한 ${selectedRows.length}명의 계정을 삭제하시겠습니까?`)) return;
    try {
      const login_ids = selectedRows.map(r => r.login_id);
      await axiosPrivate.post(`${API_URL}/auth/admin/deleteUsers`, { login_ids });
      await fetchUsers();
      setSelectedRows([]);
      alert('삭제 완료');
    } catch (err) {
      console.error(err);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const onSelectionChanged = () => {
    if (gridRef.current && gridRef.current.api) {
      const selectedNodes = gridRef.current.api.getSelectedNodes();
      const selectedData = selectedNodes.map(node => node.data);
      setSelectedRows(selectedData);
    }
  };

  // admin 행은 선택 불가 처리 (개별/전체 선택 모두)
  const isRowSelectable = (rowNode) => {
    const data = rowNode.data;
    if (!data) return true;
    return data.is_admin !== 1;
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const canCreateMoreUsers = quota.user_count < quota.user_limit;
  const subheaderText = `현재: ${quota.user_count} / 가능: ${quota.user_limit}`;

  return (
    <Grid container spacing={3} sx={{ height: "100%" }}>
      <Grid size={{ xs: 12 }} sx={{ height: "100%"}}>
        <Card sx={{
          height: "100%",
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '0',
          bgcolor: theme.palette.mode === 'dark' 
            ? 'var(--mui-palette-background-default)' 
            : 'var(--mui-palette-background-paper)'
        }}>
          <CardHeader
            title="계정 리스트"
            subheader={subheaderText}
            action={
              <Box display="flex" gap={1}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<AddIcon />}
                  onClick={handleNewUser}
                  disabled={!canCreateMoreUsers}
                >
                  신규 생성
                </Button>
                <Button 
                  variant="contained" 
                  color="error" 
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteUsers}
                  disabled={selectedRows.length === 0}
                >
                  삭제
                </Button>
              </Box>
            }
          />
          <CardContent sx={{ flexGrow: 1, overflow: "auto" }}>
            <MasterAgGrid
              gridRef={gridRef}
              rowData={users}
              columnDefs={columnDefs}
              onSelectionChanged={onSelectionChanged}
              pageSize={100}
              isRowSelectable={isRowSelectable}
            />
          </CardContent>
        </Card>
      </Grid>
      <UserDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        userData={selectedUser}
        onSuccess={async () => { await fetchUsers(); await fetchGroupQuota(); }}
      />
    </Grid>
  );
};

export default UsersPage;